import { type FC, useCallback, useId, useRef } from 'react'
import { FormattedMessage } from 'react-intl'

import { IconClose, IconSearch } from '@redteclab/icons'

import { FormControl, FormLabel, Input } from 'base-ui'

import { ALGOLIA_QUERY_MAX_LENGTH } from '../../../../../../algolia'
import { useExperimentsContext } from '../../../../../../experiments/components/ExperimentsContext'
import {
  EXPERIMENTS_TRACK_EVENT_HEADER_SEARCH_ICON,
  EXPERIMENTS_TRACK_EVENT_HEADER_SEARCH_INPUT,
  experimentsTrackEvent,
} from '../../../../../../experiments/eventTracking'
import { useGetExperimentForTenant } from '../../../../../../experiments/hooks'
import { EXPERIMENT_HEADER_STICKY_TOGGLE } from '../../../../../../experiments/model/EXPERIMENT_NAME_PER_TENANT'
import { experimentAccessorIsEnabled } from '../../../../../../experiments/model/experimentAccessor'
import { useGlobalConfigContext } from '../../../../../../global-config'
import { useRunCallbackWhenNavigated } from '../../../../../../navigation-listener'
import { useHeaderSearchContext } from '../../../headerSearchContext'
import { useHeaderSearchPlaceholderText } from '../../../useHeaderSearchPlaceholderText'
import { handleKeyPressedOnSearchInput } from '../../HeaderSearchActiveSearchBox/HeaderSearchAlgoliaContainer/HeaderSearchResultsContainer/HeaderSearchResults/handleKeyPressedOnSearchInput'

import styles from './HeaderSearchDesktopInput.module.css'

// eslint-disable-next-line max-lines-per-function -- https://jira.shop-apotheke.com/browse/CRO-4492 will be removed after the experiment ends
export const HeaderSearchDesktopInput: FC = () => {
  const headerSearchContext = useHeaderSearchContext()
  const { experiments } = useExperimentsContext()
  const { handleSubmitSearch, inputControls } = headerSearchContext
  const headerSearchPlaceholderText = useHeaderSearchPlaceholderText()
  const resultsPortal = useRef<HTMLDivElement | null>(null)
  const inputId = useId()
  const headerStickyToggleExperiment = useGetExperimentForTenant(
    EXPERIMENT_HEADER_STICKY_TOGGLE,
  )
  const globalConfig = useGlobalConfigContext()

  useRunCallbackWhenNavigated(
    useCallback(() => {
      headerSearchContext.setSearchIsActive({
        value: false,
      })
    }, [headerSearchContext]),
  )

  return (
    <>
      <form
        className="relative top-1/2 mb-4 flex -translate-y-1/2 flex-row"
        onSubmit={(event): void => {
          /**
           * We do "submitting" via js.
           * Form is left as fallback, for cases when javascript will fail to hydrate for some reason,
           * but user still be able to land on search results page.
           */
          event.preventDefault()
          handleSubmitSearch()
        }}
      >
        <FormControl
          className="w-full"
          onChange={inputControls.handleInputChange}
          value={inputControls.searchInputValue}
        >
          <FormLabel htmlFor={inputId}>{headerSearchPlaceholderText}</FormLabel>
          <Input
            autoComplete="off"
            endAdornment={
              inputControls.searchInputValue === '' ? null : (
                <button
                  onClick={() => {
                    inputControls.clearSearch()
                  }}
                  type="button"
                >
                  <IconClose className="-mr-2 size-8 text-dark-brand" />
                </button>
              )
            }
            name="query"
            onClick={(): void => {
              if (
                experimentAccessorIsEnabled(
                  headerStickyToggleExperiment.name,
                  experiments,
                )
              ) {
                experimentsTrackEvent(
                  globalConfig,
                  EXPERIMENTS_TRACK_EVENT_HEADER_SEARCH_INPUT,
                )
              }
            }}
            onFocus={(): void => {
              headerSearchContext.setSearchIsActive({
                portalResultsToElement: resultsPortal.current ?? undefined,
                value: true,
              })
            }}
            onKeyUp={(event): void => {
              handleKeyPressedOnSearchInput(event, headerSearchContext)
            }}
            slotProps={{
              input: {
                className: styles['header-search-desktop-input__input'],
                'data-qa-id': 'search-box-query',
                id: inputId,
                maxLength: ALGOLIA_QUERY_MAX_LENGTH,
              },
              root: {
                className:
                  'flex items-center rounded-l-full rounded-r-none border-y border-l border-dark-brand bg-light-primary-medium hover:border-dark-brand transition-colors',
              },
            }}
            type="search"
          />
        </FormControl>
        <button
          className="flex items-center whitespace-nowrap rounded-r-full bg-dark-brand px-3.5 text-m text-light-primary-strong hover:bg-dark-pressed-brand"
          data-qa-id="search-box-query-button"
          onClick={() => {
            if (
              experimentAccessorIsEnabled(
                headerStickyToggleExperiment.name,
                experiments,
              )
            ) {
              experimentsTrackEvent(
                globalConfig,
                EXPERIMENTS_TRACK_EVENT_HEADER_SEARCH_ICON,
              )
            }
          }}
          type="submit"
        >
          <IconSearch className="mr-2.5 size-8 fill-light-primary-strong" />

          <span className="sr-only font-medium desktop:not-sr-only">
            <FormattedMessage id="general.button.label.search" />
          </span>
        </button>
      </form>
      <div ref={resultsPortal} />
    </>
  )
}
